import { useCallback, useRef } from "react";

// Deck-owned, id-keyed ref map for swipe cards rendered inside AnimatePresence.
//
// Why not a single shared useRef passed to every top card: when a card is
// swiped, the NEXT card mounts and attaches its imperative handle to the
// shared ref, but the OLD card stays mounted for its exit animation. When the
// old card finally unmounts (about 0.3s later), React runs ITS ref cleanup
// and nulls the shared ref, clobbering the live card's handle. Result: the
// action buttons work exactly once, then hit null forever (drags keep working
// because they never touch the ref). Keying the map by card id means an
// exiting card can only ever remove its OWN entry, never the current top's.
export default function useCardRefs() {
  const handles = useRef(new Map()); // id -> imperative handle ({ swipe })
  const binders = useRef(new Map()); // id -> stable ref callback for that id

  // Stable per-id ref callback, so re-renders never detach a live handle.
  const bindCard = useCallback((id) => {
    if (!binders.current.has(id)) {
      binders.current.set(id, (handle) => {
        if (handle) {
          handles.current.set(id, handle);
        } else {
          handles.current.delete(id);
          binders.current.delete(id);
        }
      });
    }
    return binders.current.get(id);
  }, []);

  const getCard = useCallback((id) => (id == null ? undefined : handles.current.get(id)), []);

  return { bindCard, getCard };
}
